import React, { useEffect, useRef, useState } from "react";

import { ArrowRight, Lightbulb, PlayCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import EntityConstants from "../../../configs/EntityConstants";
import { cn } from "../../../lib/utils";
import { GET } from "../../../service/CRUDService";
import { automationTestIds } from "../../../testing/automationTestIds";
import { isValidHttpUrl } from "../../../utils/urlValidation";
import CredentialHeroArt from "../components/CredentialHeroArt";

const SHOWCASE_LIMIT = 4;

const PANEL_HEIGHT = 168;

const PANEL_BACKGROUND =
  "linear-gradient(145deg, color-mix(in srgb, var(--cf-primary-color) 8%, white) 0%, color-mix(in srgb, var(--cf-primary-color) 3%, white) 100%)";

const SKELETON_SLOTS = Array.from(
  { length: SHOWCASE_LIMIT },
  (_, slot) => `skeleton-template-${slot}`,
);

const CategorySkeleton = () => {
  const { t } = useTranslation(["onboarding"]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4"
    >
      {SKELETON_SLOTS.map((slot) => (
        <div
          key={slot}
          className="overflow-hidden rounded-2xl border-[1px] border-cfCardBorder bg-cfSecondary"
        >
          <div
            className="w-full animate-pulse bg-gray-200"
            style={{ height: PANEL_HEIGHT }}
          />
          <div className="p-4">
            <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
            <div className="mt-2.5 h-3 w-full animate-pulse rounded bg-gray-200" />
            <div className="mt-1.5 h-3 w-4/5 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      ))}
      <span className="sr-only">{t("onboarding:loading")}</span>
    </div>
  );
};

const CategoryArtwork = () => (
  <svg viewBox="0 0 120 74" aria-hidden="true" className="h-[88px] w-[142px]">
    <rect
      x="22"
      y="4"
      width="76"
      height="66"
      rx="5"
      fill="#fff"
      stroke="var(--cf-primary-color)"
      strokeOpacity="0.18"
    />
    <path
      d="M22 9a5 5 0 0 1 5-5h66a5 5 0 0 1 5 5v4H22z"
      fill="var(--cf-primary-color)"
    />
    <circle
      cx="60"
      cy="27"
      r="7"
      fill="none"
      stroke="#D8A945"
      strokeWidth="1.6"
    />
    <path
      d="M57 27.5l2 2 4-4.5"
      fill="none"
      stroke="#D8A945"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect
      x="38"
      y="41"
      width="44"
      height="4"
      rx="2"
      fill="var(--cf-inactive-surface-color)"
    />
    <rect x="46" y="49" width="28" height="3.5" rx="1.75" fill="#EFF1F5" />
    <rect
      x="34"
      y="59"
      width="20"
      height="7"
      rx="2"
      fill="var(--cf-disabled-light-color)"
    />
    <rect
      x="66"
      y="59"
      width="20"
      height="7"
      rx="2"
      fill="var(--cf-disabled-light-color)"
    />
  </svg>
);

const getImageSources = (template) => {
  const preview =
    typeof template?.thumbnail_svg_url === "string"
      ? template.thumbnail_svg_url.trim()
      : "";
  return preview ? [{ url: preview, kind: "preview" }] : [];
};

const CategoryPreview = ({ sources }) => {
  const [failedCount, setFailedCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  const current = sources[failedCount] || null;

  useEffect(() => {
    setLoaded(false);
    const node = imgRef.current;
    if (node && node.complete && node.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [current?.url]);

  return (
    <span
      className="relative flex w-full items-center justify-center overflow-hidden"
      style={{ height: PANEL_HEIGHT, background: PANEL_BACKGROUND }}
    >
      {current ? (
        <>
          {!loaded ? (
            <span
              aria-hidden="true"
              className="absolute inset-0 animate-pulse"
              style={{
                background:
                  "color-mix(in srgb, var(--cf-primary-color) 7%, white)",
              }}
            />
          ) : null}
          <img
            key={current.url}
            ref={imgRef}
            src={current.url}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setFailedCount((count) => count + 1)}
            className={cn(
              "object-contain transition-[opacity,transform] duration-500 ease-out group-hover:scale-[1.05]",
              loaded ? "opacity-100" : "opacity-0",
              current.kind === "icon"
                ? "h-[116px] w-[116px]"
                : "max-h-[140px] max-w-[84%] rounded-[6px] shadow-[0_8px_20px_-8px_rgba(16,24,40,0.3),0_0_0_1px_rgba(16,24,40,0.06)]",
            )}
          />
        </>
      ) : (
        <span className="transition-transform duration-300 group-hover:scale-105">
          <CategoryArtwork />
        </span>
      )}
    </span>
  );
};

const TemplateShowcaseRow = ({ templates, isLoading, onSelect }) => {
  const { t } = useTranslation(["onboarding"]);

  if (isLoading) {
    return <CategorySkeleton />;
  }

  if (!templates.length) {
    return null;
  }

  return (
    <div
      id={automationTestIds.onboardingRecommendedRow}
      className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4"
    >
      {templates.map((template) => (
        <button
          key={template.credential_template_id}
          type="button"
          id={`onboarding-recommended-${template.credential_template_id}`}
          aria-label={t("onboarding:welcome_category_open", {
            name: template.name,
          })}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onSelect(template)}
          style={{ outline: "none" }}
          className={cn(
            "group flex flex-col overflow-hidden rounded-2xl border-[1px] border-cfCardBorder bg-cfSecondary text-left",
            "transition-[transform,box-shadow,border-color] duration-200 ease-out",
            "hover:-translate-y-1 hover:border-[color-mix(in_srgb,var(--cf-primary-color)_35%,white)] hover:shadow-[0_14px_28px_-12px_rgba(16,24,40,0.22)]",
            "focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2",
          )}
        >
          <CategoryPreview sources={getImageSources(template)} />

          <span className="flex flex-1 flex-col p-4">
            <span className="block text-[15px] font-semibold leading-snug text-cfHeadingText">
              {template.name}
            </span>
            <span className="mt-auto flex items-end justify-between gap-3 pt-1.5">
              <span className="min-w-0 text-[13px] leading-snug text-cfGlobalText line-clamp-2">
                {template.description}
              </span>
              <ArrowRight
                className="h-4 w-4 shrink-0 translate-y-[-1px] text-cfPrimaryButton transition-transform duration-200 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </span>
          </span>
        </button>
      ))}
    </div>
  );
};

const OnboardingHelpCard = () => {
  const { t } = useTranslation(["onboarding"]);

  const readHelpUrl = (value) => (isValidHttpUrl(value) ? value.trim() : null);

  const tourUrl = readHelpUrl(process.env.REACT_APP_ONBOARDING_TOUR_URL);
  const guideUrl = readHelpUrl(process.env.REACT_APP_ONBOARDING_GUIDE_URL);

  if (!tourUrl && !guideUrl) {
    return null;
  }

  return (
    <section
      id={automationTestIds.onboardingHelpCard}
      className="flex flex-col gap-4 rounded-2xl border-[1px] border-cfCardBorder bg-cfSecondary p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cfPrimaryMuted">
          <Lightbulb
            className="h-5 w-5 text-cfPrimary"
            strokeWidth={1.75}
            aria-hidden="true"
          />
        </span>
        <div>
          <p className="text-[15px] font-semibold text-cfHeadingText">
            {t("onboarding:welcome_help_title")}
          </p>
          <p className="mt-0.5 text-sm text-cfGlobalText">
            {t("onboarding:welcome_help_subtitle")}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {tourUrl ? (
          <a
            id={automationTestIds.onboardingHelpTour}
            href={tourUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-[8px] border-[1px] border-cfSignInSignUpInputBorder bg-white px-4 py-2 text-sm font-medium text-cfHeadingText transition-colors hover:bg-cfTableHeaderBackgroundColor focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2"
          >
            <PlayCircle className="h-4 w-4" aria-hidden="true" />
            {t("onboarding:welcome_help_tour")}
          </a>
        ) : null}

        {guideUrl ? (
          <a
            id={automationTestIds.onboardingHelpGuide}
            href={guideUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-[8px] px-4 py-2 text-sm font-medium text-cfPrimaryButton transition-colors hover:bg-[color-mix(in_srgb,var(--cf-primary-color)_8%,white)] focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2"
          >
            {t("onboarding:welcome_help_guide")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        ) : null}
      </div>
    </section>
  );
};

export const templatePath = (template) =>
  `/certificate/edit/${template.credential_template_id}`;

const WelcomeStep = () => {
  const { t } = useTranslation(["onboarding"]);
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      let results = [];
      try {
        // Authenticated: GET attaches Authorization: Bearer <auth_token>.
        const response = await GET(
          EntityConstants.DISCOVERY_SELECTED_TEMPLATES,
        );
        if (response?.ok) {
          const data = await response.json();
          results = Array.isArray(data?.results)
            ? data.results.slice(0, SHOWCASE_LIMIT)
            : [];
        }
      } catch (error) {
        console.error(error);
      }
      if (cancelled) {
        return;
      }
      setTemplates(results);
      setIsLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const leaveTo = (path) => navigate(path);

  const featuredTemplate =
    templates.find((template) => getImageSources(template).length > 0) ||
    templates[0] ||
    null;
  const featuredPreview = featuredTemplate
    ? getImageSources(featuredTemplate)[0]?.url || ""
    : "";
  const openFeatured = () =>
    leaveTo(featuredTemplate ? templatePath(featuredTemplate) : "/designs");

  return (
    <div className="flex flex-col gap-10">
      <section className="relative overflow-hidden rounded-2xl border-[1px] border-cfCardBorder bg-cfSecondary shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
        <div className="flex flex-col items-center gap-8 p-6 md:flex-row md:justify-between md:gap-10">
          <div className="max-w-md">
            <h2 className="text-2xl font-semibold leading-tight text-cfHeadingText sm:text-[28px]">
              {t("onboarding:welcome_hero_title")}
            </h2>
            <p className="mt-2.5 text-[15px] leading-relaxed text-cfGlobalText">
              {t("onboarding:welcome_hero_subtitle")}
            </p>
            <button
              type="button"
              id={automationTestIds.onboardingWelcomeHeroCta}
              onMouseDown={(event) => event.preventDefault()}
              onClick={openFeatured}
              style={{ outline: "none" }}
              className="mt-7 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[8px] bg-cfPrimaryButton px-5 py-2.5 text-sm font-medium text-white transition-[background-color,transform,box-shadow] duration-200 hover:bg-cfPrimaryDark hover:shadow-[0_8px_18px_-8px_color-mix(in_srgb,var(--cf-primary-color)_70%,transparent)] active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2"
            >
              {t("onboarding:welcome_hero_cta")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {featuredTemplate ? (
            <button
              type="button"
              id={automationTestIds.onboardingWelcomeHeroPreview}
              aria-label={t("onboarding:welcome_category_open", {
                name: featuredTemplate.name,
              })}
              onMouseDown={(event) => event.preventDefault()}
              onClick={openFeatured}
              style={{ outline: "none" }}
              className="w-full max-w-[26rem] shrink-0 rounded-[14px] md:max-w-[28rem] lg:max-w-[32rem] focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2"
            >
              <CredentialHeroArt
                className="w-full"
                previewSrc={featuredPreview}
              />
            </button>
          ) : (
            <CredentialHeroArt className="w-full max-w-[26rem] shrink-0 md:max-w-[28rem] lg:max-w-[32rem]" />
          )}
        </div>
      </section>

      <section>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-cfHeadingText">
              {t("onboarding:welcome_recommended_title")}
            </h2>
            <p className="mt-1 text-sm text-cfGlobalText">
              {t("onboarding:welcome_recommended_subtitle")}
            </p>
          </div>
          <button
            type="button"
            id={automationTestIds.onboardingWelcomeViewAll}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => leaveTo("/designs")}
            style={{ outline: "none" }}
            className="group inline-flex shrink-0 items-center gap-1.5 rounded-[8px] px-2 py-1 text-sm font-medium text-cfPrimaryButton transition-colors hover:underline focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2"
          >
            {t("onboarding:welcome_view_all")}
            <ArrowRight
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
              aria-hidden="true"
            />
          </button>
        </div>

        <div className="mt-5">
          <TemplateShowcaseRow
            templates={templates}
            isLoading={isLoading}
            onSelect={(template) => leaveTo(templatePath(template))}
          />
        </div>
      </section>

      <OnboardingHelpCard />
    </div>
  );
};

export default WelcomeStep;
